import * as Ant from "antd";
import { Option, pipe, ReadonlyArray, Runtime } from "fp";

import Moment from "moment-timezone";
import React from "react";

import { type, creator, reducer } from "~/Action";
import * as GenericAction from "~/Action";
import { Company } from "~/Company";
import * as Driver from "~/Driver";
import * as Icons from "~/Icon";
import { ID } from "~/ID";
import * as Layout from "~/Layout";
import { Order } from "~/Order";
import { Remote } from "~/Remote";
import { Request } from "~/Request";
import { useSelector } from "~/State";
import { useLocalStorage } from "react-use";

export type Zone = { readonly zone: string };
export function Zone({ hideIcon, ...zone }: Zone.Props) {
  return (
    <>
      {!hideIcon && <Icons.Clock faded />}
      <Layout.Inline.Component left={!hideIcon}>
        {Zone.toString(zone)}
      </Layout.Inline.Component>
    </>
  );
}

export namespace Zone {
  export type Props = Zone & { readonly hideIcon?: boolean };

  export const list = () =>
    pipe(
      [...Moment.tz.zonesForCountry("US"), ...Moment.tz.zonesForCountry("CA")],
      ReadonlyArray.sort(Order.string()),
      ReadonlyArray.map((name) => ({ zone: name }))
    );

  export const toString = ({ zone }: Zone) => zone.replace(/_/g, " ");
  export const fromString = (string: string): Zone | undefined =>
    Moment.tz.zone(string) ? { zone: string } : undefined;

  export const fromPreferences = (
    userId: number | string
  ): Zone | undefined => {
    const rawValue = window.localStorage.getItem(`timezone-${userId}`);
    return rawValue ? fromString(JSON.parse(rawValue)) : undefined;
  };
  export const usePreferences = (userId: number | string) => {
    const [value, set, remove] = useLocalStorage<string>(`timezone-${userId}`);
    return [
      value ? fromString(value) : undefined,
      (zone?: Zone) => (zone ? set(zone.zone) : remove()),
      remove,
    ] as const;
  };

  export const toOffset = (
    { zone }: Zone = { zone: Moment.tz.guess() },
    date: number = Date.now()
  ) => Moment.tz.zone(zone)?.utcOffset(date) ?? 0;

  export const local = (): Zone => ({ zone: Moment.tz.guess() });

  export const use = (): State =>
    useSelector({
      select: ({ time }) => time,
      equals: (a) => (b) => a.zone === b.zone,
    });

  export type State = Zone;
  export namespace State {
    export const initial = (userId: number): State => {
      return fromPreferences(userId) || { zone: Moment.tz.guess() };
    };
  }

  export namespace Action {
    export const { TYPE, create, reduce } = pipe(
      type("Time zone updated" as const),
      creator((payload: Zone) => payload),
      reducer(({ state, action: { zone } }) => (state.time.zone = zone))
    );
  }

  export namespace Default {
    export const use = (user: ID): Remote<Zone | undefined> =>
      Remote.Query.use("default-time-zone", request(user));

    export const useSet = (user: ID): void => {
      const dispatch = GenericAction.useDispatch();

      const [preferenceZone] = usePreferences(user.id);
      const remoteZone = Remote.toUndefined(use(user));
      const zone = preferenceZone || remoteZone;

      // A driver-scoped page dispatches the driver's own time zone once it
      // loads (see `DriverTimezoneMismatchWarning`) — skip the viewing user's
      // own preference there, since it's an independent, unordered fetch that
      // could otherwise resolve first on a hard reload and flash the wrong
      // zone before the driver's own zone takes over. Resolved synchronously
      // from the route, so this never itself races anything.
      const onDriverRoute = Option.isSome(Driver.useReferenceFromRoute());

      React.useEffect(() => {
        if (onDriverRoute) return;
        zone && dispatch(Action.create(zone));
      }, [zone?.zone, onDriverRoute]);
    };

    export const request = (user: ID): Request.Fn<Zone | undefined> =>
      flow(
        Request.fromConfig({ endpoint: `/users/${user.id}` }),
        Request.Expect.status(200),
        Request.Expect.json(
          Runtime.strict({ timezone: Runtime.nullable(Runtime.string()) })
        ),
        Request.onSuccess((response) =>
          response.timezone ? { zone: response.timezone } : undefined
        )
      );
  }

  export function Select({ selected, select }: Select.Props) {
    const company = pipe(Company.use(), ({ company }) =>
      pipe(
        company,
        Remote.map(({ time }) => ({ time })),
        Remote.getOrElse(() => ({ time: undefined as Zone | undefined }))
      )
    );

    const driver = Driver.useFromReference({
      ...Option.toUndefined(Driver.useReferenceFromRoute()),
      found: (driver) => driver,
      missing: () => undefined,
    });

    const presets = [
      ["Local Time Zone", "Local Time Zone", <Icons.Location faded />],
      company.time?.zone && [
        "Company Time Zone",
        "Company Time Zone",
        <Icons.Home faded />,
      ],
      driver?.time && [
        "Driver Time Zone",
        "Driver Time Zone",
        <Icons.UserHardHat faded />,
      ],
    ];

    const zones = React.useMemo(
      () =>
        pipe(
          list(),
          ReadonlyArray.map(({ zone }) => [
            zone,
            toString({ zone }),
            <Icons.Clock faded />,
          ])
        ),
      []
    );

    const options = pipe(
      [...presets, ...zones],
      ReadonlyArray.filterMap(
        flow(
          Option.fromNullable,
          Option.map(([value, label, icon]) => ({
            value,
            label: (
              <>
                {icon}
                <Layout.Inline.Component left>{label}</Layout.Inline.Component>
              </>
            ),
          }))
        )
      )
    );

    return (
      <Ant.Select
        allowClear={false}
        dropdownMatchSelectWidth={false}
        value={selected?.zone}
        options={options as React.ComponentProps<typeof Ant.Select>["options"]}
        onChange={(value) =>
          value === "Company Time Zone" && company.time
            ? select(company.time)
            : value === "Driver Time Zone" && driver?.time
            ? select(driver.time)
            : value === "Local Time Zone"
            ? select({ zone: Moment.tz.guess() })
            : select({ zone: value.replace(" ", "_") })
        }
      />
    );
  }

  export namespace Select {
    export type Props = {
      readonly selected?: Zone;
      readonly select: (zone: Zone) => void;
    };

    export const use = (initial: Zone = { zone: Moment.tz.guess() }): Props => {
      const [selected, select] = React.useState(initial);
      return { selected, select };
    };

    export const initialize = ({ select }: Props) => (
      initial: Zone = { zone: Moment.tz.guess() }
    ): void => {
      select(initial);
    };
  }
}
